import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Calendar, 
  Clock, 
  MapPin, 
  ChevronRight,
  Leaf,
  Sparkles,
  Heart,
  Star,
  Check,
  Phone,
  Shield
} from 'lucide-react';
import { 
  businessInfo, 
  services, 
  testimonials, 
  faqs, 
  threeSteps,
  images,
  pureRenewalProtocol 
} from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { 
  CherryBlossom, 
  BlossomBranch, 
  Sparkle, 
  DotsPattern,
  WaveDecoration 
} from '../components/ui/FloralDecorations';

const HomePage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section - Pink Gradient Background */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" style={{
        background: 'linear-gradient(135deg, #FCF0F0 0%, #F5E1E1 25%, #FFF9F7 50%, #E8C4C4 100%)'
      }}>
        {/* Decorative floating flowers */}
        <div className="absolute top-20 left-10 animate-float-slow opacity-40">
          <CherryBlossom size={80} color="#D4A5A5" />
        </div>
        <div className="absolute top-40 right-20 animate-float opacity-30" style={{ animationDelay: '1s' }}>
          <CherryBlossom size={60} color="#E8C4C4" />
        </div>
        <div className="absolute bottom-40 left-1/4 animate-float-slow opacity-25" style={{ animationDelay: '2s' }}>
          <CherryBlossom size={70} color="#D4A5A5" />
        </div>
        <div className="absolute top-1/3 right-1/3 animate-pulse-soft">
          <Sparkle size={24} color="#D4A5A5" className="opacity-40" />
        </div>
        <div className="absolute bottom-1/3 left-1/3 animate-pulse-soft" style={{ animationDelay: '1.5s' }}>
          <Sparkle size={18} color="#6E9EB9" className="opacity-30" />
        </div>
        
        {/* Branch decorations */}
        <div className="absolute top-0 left-0 opacity-20">
          <BlossomBranch />
        </div>
        <div className="absolute bottom-0 right-0 opacity-20 rotate-180">
          <BlossomBranch flip />
        </div>
        
        {/* Dot pattern */}
        <div className="absolute bottom-20 right-10 opacity-20">
          <DotsPattern />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              {/* Tagline Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full mb-6 border border-[#D4A5A5]/30 shadow-sm">
                <Sparkles size={16} className="text-[#D4A5A5]" />
                <span className="text-sm font-medium text-[#2C2F36]">
                  Traditional Naturopathy in Gordon, NE
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-[#2C2F36] mb-6 leading-tight">
                Nutritional Therapy...
                <span className="block mt-2" style={{ color: '#D4A5A5' }}>
                  A Peaceful Path to Healing
                </span>
              </h1>
              
              <p className="text-lg text-[#4A5568] mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Helping the sick recover by nourishing the body with abundant, high-quality nutrients. 
                Experience personalized care at Serenity Natural Health & Wellness.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/contact" className="btn-pink">
                  Book Consultation
                  <ArrowRight size={18} />
                </Link>
                <Link to="/orientation" className="btn-secondary">
                  Free Orientation Class
                </Link>
              </div>
              
              {/* Trust Indicators */}
              <div className="mt-10 pt-8 border-t border-[#D4A5A5]/20">
                <div className="flex flex-wrap justify-center lg:justify-start gap-6 text-sm text-[#4A5568]">
                  <div className="flex items-center gap-2">
                    <Check size={18} className="text-[#D4A5A5]" />
                    <span>ANMA Board Certified</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={18} className="text-[#D4A5A5]" />
                    <span>Personalized Care</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check size={18} className="text-[#D4A5A5]" />
                    <span>Natural Approach</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Hero Image with 3D effect */}
            <div className="relative">
              <div className="relative z-10 card-3d">
                <div className="card-3d-inner">
                  <img 
                    src={images.owner1}
                    alt="Julie Hilliker - Traditional Naturopath"
                    className="rounded-3xl shadow-2xl w-full max-w-md mx-auto object-cover"
                    style={{ boxShadow: '0 25px 50px -12px rgba(212, 165, 165, 0.4)' }}
                  />
                </div>
              </div>
              {/* Decorative frames */}
              <div className="absolute -top-4 -right-4 w-full h-full max-w-md mx-auto border-2 border-[#D4A5A5]/40 rounded-3xl hidden lg:block" />
              <div className="absolute -bottom-4 -left-4 w-full h-full max-w-md mx-auto rounded-3xl hidden lg:block" style={{ background: 'linear-gradient(135deg, rgba(212, 165, 165, 0.3), rgba(232, 196, 196, 0.2))' }} />
              
              {/* Floating flowers around image */}
              <div className="absolute -top-8 -right-8 animate-float opacity-60">
                <CherryBlossom size={50} />
              </div>
              <div className="absolute -bottom-4 -left-8 animate-float-slow opacity-50" style={{ animationDelay: '1s' }}>
                <CherryBlossom size={40} color="#E8C4C4" />
              </div>
              
              {/* Floating Card - Positioned to the right to avoid overlap */}
              <div className="absolute -bottom-6 -right-6 bg-white/95 backdrop-blur-sm p-4 rounded-2xl shadow-lg hidden md:block border border-[#D4A5A5]/20 z-20 min-w-[200px]">
                <p className="font-serif text-lg text-[#2C2F36]">{businessInfo.owner.name}</p>
                <p className="text-sm" style={{ color: '#D4A5A5' }}>{businessInfo.owner.credentials}</p>
                <p className="text-xs text-[#718096] mt-1">{businessInfo.owner.title}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Start Here - 3 Steps with pink accents */}
      <section className="section-padding relative" style={{ background: 'linear-gradient(180deg, white 0%, #FCF0F0 100%)' }}>
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 opacity-20">
          <CherryBlossom size={100} />
        </div>
        <div className="absolute bottom-10 left-10 opacity-15">
          <BlossomBranch />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(212, 165, 165, 0.2)', color: '#D4A5A5' }}>
              Your Wellness Journey
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              3 Simple Steps to Gaining and Maintaining Health
            </h2>
            <div className="floral-divider w-24 mx-auto mb-6" />
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              Start your path to wellness with our proven approach to natural health.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {threeSteps.map((step, index) => (
              <div 
                key={step.step}
                className="relative text-center p-8 rounded-3xl card-elevated"
                style={{ background: 'linear-gradient(180deg, white 0%, #FCF0F0 100%)' }}
              >
                <div 
                  className="absolute -top-5 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg text-white shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #D4A5A5, #E8C4C4)' }}
                >
                  {step.step}
                </div>
                <div className="mt-4">
                  <h3 className="text-xl font-serif font-medium text-[#2C2F36] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-[#4A5568]">{step.description}</p>
                </div>
                {index < threeSteps.length - 1 && (
                  <ChevronRight 
                    size={24} 
                    className="hidden md:block absolute top-1/2 -right-4 -translate-y-1/2 z-10" 
                    style={{ color: '#D4A5A5' }}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/orientation" className="btn-pink">
              Start Your Journey
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Clinic Gallery Section - Expanded */}
      <section className="section-padding" style={{ background: 'linear-gradient(180deg, #FCF0F0 0%, #F5E1E1 50%, #FCF0F0 100%)' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(110, 158, 185, 0.15)', color: '#6E9EB9' }}>
              Our Space
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              Welcome to Serenity
            </h2>
            <div className="floral-divider w-24 mx-auto mb-6" />
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              Step into our peaceful wellness sanctuary, designed to support your healing journey.
            </p>
          </div>

          {/* Image Gallery with 3D Cards - Expanded */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="lg:col-span-2 card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src={images.wellnessArea1}
                  alt="Serenity Clinic Wellness Area"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-serif text-xl">Main Wellness Area</p>
                  <p className="text-sm opacity-80">Yoga & Class Space</p>
                </div>
              </div>
            </div>
            
            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src={images.cryotherapyRoom}
                  alt="Cryotherapy Room"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-serif text-lg">Cryotherapy</p>
                  <p className="text-xs opacity-80">Cold Therapy Room</p>
                </div>
              </div>
            </div>
            
            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src={images.infraredSauna}
                  alt="Infrared Sauna"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-serif text-lg">Infrared Sauna</p>
                  <p className="text-xs opacity-80">Deep Heat Therapy</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Second row of images */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-72 overflow-hidden">
                <img 
                  src={images.consultationArea}
                  alt="Consultation Area"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-serif text-lg">Consultation Area</p>
                  <p className="text-xs opacity-80">Cozy Office Space</p>
                </div>
              </div>
            </div>
            
            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-72 overflow-hidden">
                <img 
                  src={images.dogOnChair}
                  alt="Relaxation Area"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-serif text-lg">Relaxation Area</p>
                  <p className="text-xs opacity-80">Even our furry friends relax here!</p>
                </div>
              </div>
            </div>
            
            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-72 overflow-hidden">
                <img 
                  src={images.wellnessArea2}
                  alt="Wellness Room"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-serif text-lg">Wellness Room</p>
                  <p className="text-xs opacity-80">Class & Event Space</p>
                </div>
              </div>
            </div>
            
            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-72 overflow-hidden">
                <img 
                  src={images.cherryBlossom}
                  alt="Peaceful Wellness Space"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-serif text-lg">Peaceful Space</p>
                  <p className="text-xs opacity-80">Cherry Blossom Decor</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Third row */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={images.massageChair}
                  alt="Massage Chair"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-serif text-lg">Massage Chair</p>
                  <p className="text-xs opacity-80">Therapeutic Relaxation</p>
                </div>
              </div>
            </div>
            
            <div className="card-elevated rounded-3xl overflow-hidden group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={images.cryotherapy}
                  alt="Therapy Equipment"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-serif text-lg">Therapy Suite</p>
                  <p className="text-xs opacity-80">Advanced Equipment</p>
                </div>
              </div>
            </div>
            
            <div className="card-elevated rounded-3xl overflow-hidden p-6 flex flex-col justify-center items-center text-center" style={{ background: 'linear-gradient(135deg, #D4A5A5, #E8C4C4)' }}>
              <CherryBlossom size={60} color="white" className="mb-4 opacity-80" />
              <p className="font-serif text-xl text-white mb-2">Experience Serenity</p>
              <p className="text-white/80 text-sm mb-4">Book a visit to see our beautiful wellness center</p>
              <Link to="/contact" className="px-5 py-2.5 bg-white text-[#D4A5A5] rounded-full text-sm font-medium hover:bg-white/90 transition-all hover:scale-105 shadow-lg">
                Schedule Visit
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services Preview */}
      <section className="section-padding bg-white relative">
        {/* Decorative flowers */}
        <div className="absolute top-20 left-5 opacity-15">
          <CherryBlossom size={80} />
        </div>
        <div className="absolute bottom-20 right-5 opacity-10">
          <BlossomBranch flip />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(212, 165, 165, 0.2)', color: '#D4A5A5' }}>
                Primary Focus
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-6">
                Nutritional Therapy: The Foundation for Healing
              </h2>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                At Serenity Natural Health & Wellness, we believe that proper nutrition is the cornerstone 
                of true healing. Our personalized nutritional therapy protocols are designed to address 
                the root causes of health concerns, not just mask symptoms.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-full mt-1" style={{ background: 'rgba(212, 165, 165, 0.2)' }}>
                    <Check size={16} style={{ color: '#D4A5A5' }} />
                  </div>
                  <span className="text-[#4A5568]">Comprehensive health assessment and personalized protocols</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-full mt-1" style={{ background: 'rgba(212, 165, 165, 0.2)' }}>
                    <Check size={16} style={{ color: '#D4A5A5' }} />
                  </div>
                  <span className="text-[#4A5568]">Targeted dietary strategies for improved health</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-full mt-1" style={{ background: 'rgba(212, 165, 165, 0.2)' }}>
                    <Check size={16} style={{ color: '#D4A5A5' }} />
                  </div>
                  <span className="text-[#4A5568]">Herbal remedies and high-quality supplements</span>
                </li>
              </ul>
              <Link to="/nutritional-therapy" className="btn-pink">
                Learn About Nutritional Therapy
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <div className="card-elevated rounded-3xl overflow-hidden">
                <img 
                  src={images.clinic}
                  alt="Serenity Natural Health & Wellness Clinic"
                  className="w-full"
                />
              </div>
              {/* Overlay card */}
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl max-w-xs hidden md:block border border-[#D4A5A5]/20">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-full" style={{ background: 'rgba(212, 165, 165, 0.2)' }}>
                    <Leaf size={24} style={{ color: '#D4A5A5' }} />
                  </div>
                  <span className="font-serif text-lg text-[#2C2F36]">Natural Approach</span>
                </div>
                <p className="text-sm text-[#4A5568]">
                  We focus on supporting your body's innate healing abilities.
                </p>
              </div>
              {/* Floating flower */}
              <div className="absolute -top-4 -left-4 animate-float">
                <CherryBlossom size={50} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detox Support Preview - Pink themed */}
      <section className="section-padding relative" style={{ background: 'linear-gradient(135deg, #FCF0F0 0%, #F5E1E1 50%, white 100%)' }}>
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full">
          <WaveDecoration color="#D4A5A5" className="w-full h-16" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(160, 140, 168, 0.2)', color: '#A08CA8' }}>
              Comprehensive Support
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              Detox & Restoration Services
            </h2>
            <div className="floral-divider w-24 mx-auto mb-6" />
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              We emphasize comprehensive detoxification support to help restore balance to your body.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.detox.items.map((service) => (
              <div 
                key={service.id}
                className="bg-white p-6 rounded-2xl card-elevated border border-[#D4A5A5]/10"
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(212, 165, 165, 0.15)' }}>
                  <Sparkles size={24} style={{ color: '#D4A5A5' }} />
                </div>
                <h3 className="font-serif text-lg font-medium text-[#2C2F36] mb-2">
                  {service.name}
                </h3>
                <p className="text-sm text-[#4A5568]">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/detox-support" className="btn-secondary">
              Explore Detox Support
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Pure Renewal Protocol Section - Featured */}
      <section className="section-padding relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #E8F4F8 0%, #F0F7F4 50%, #E8F4F8 100%)' }}>
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 opacity-20">
          <CherryBlossom size={80} color="#0D7377" />
        </div>
        <div className="absolute bottom-10 left-10 opacity-15">
          <BlossomBranch />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Protocol Image */}
            <div className="relative order-2 lg:order-1">
              <div className="card-elevated rounded-3xl overflow-hidden bg-white p-4">
                <img 
                  src={images.pureRenewalLogo}
                  alt="The Pure Renewal Protocol - Natural Detox Program by Serenity Natural Health & Wellness"
                  className="w-full rounded-2xl"
                />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-4 -right-4 bg-white p-4 rounded-2xl shadow-lg hidden md:block border border-[#0D7377]/20">
                <div className="flex items-center gap-2">
                  <Shield size={20} className="text-[#0D7377]" />
                  <span className="font-medium text-[#2C2F36]">Available Remotely</span>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="order-1 lg:order-2">
              <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(13, 115, 119, 0.15)', color: '#0D7377' }}>
                Featured Program
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
                {pureRenewalProtocol.tagline}
              </h2>
              <h3 className="text-2xl font-serif mb-4" style={{ color: '#0D7377' }}>
                The Pure Renewal Protocol
              </h3>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                {pureRenewalProtocol.description}
              </p>
              
              {/* Benefits list */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                {pureRenewalProtocol.benefits.slice(0, 6).map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="p-1 rounded-full" style={{ background: 'rgba(13, 115, 119, 0.15)' }}>
                      <Check size={14} className="text-[#0D7377]" />
                    </div>
                    <span className="text-sm text-[#4A5568]">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/pure-renewal-protocol" 
                  className="px-6 py-3 rounded-full font-semibold transition-all hover:scale-105 inline-flex items-center justify-center gap-2 shadow-lg text-white"
                  style={{ background: 'linear-gradient(135deg, #0D7377, #14A3A8)' }}
                >
                  Learn More
                  <ArrowRight size={18} />
                </Link>
                <a 
                  href={`tel:${businessInfo.phone.replace(/[^0-9]/g, '')}`}
                  className="px-6 py-3 bg-white border-2 border-[#0D7377] text-[#0D7377] rounded-full font-semibold hover:bg-[#0D7377]/5 transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Phone size={18} />
                  {businessInfo.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wellness Therapies Preview */}
      <section className="section-padding bg-white relative">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(110, 158, 185, 0.15)', color: '#6E9EB9' }}>
              Add-On Services
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              Wellness Therapies
            </h2>
            <div className="floral-divider w-24 mx-auto mb-6" />
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              Complement your healing journey with our range of supportive wellness therapies.
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
            {services.wellness.items.slice(0, 7).map((service) => (
              <div 
                key={service.id}
                className="p-4 rounded-2xl flex items-center gap-3 transition-all duration-300 hover:shadow-md border border-transparent hover:border-[#D4A5A5]/20"
                style={{ background: 'linear-gradient(135deg, #FCF0F0 0%, white 100%)' }}
              >
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Heart size={18} style={{ color: '#D4A5A5' }} />
                </div>
                <span className="font-medium text-[#2C2F36] text-sm">{service.name}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/services" className="btn-secondary">
              View All Services
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials - Pink themed */}
      <section className="section-padding relative" style={{ background: 'linear-gradient(180deg, #FCF0F0 0%, #F5E1E1 50%, #FCF0F0 100%)' }}>
        {/* Decorative flowers */}
        <div className="absolute top-10 left-10 opacity-20 animate-float-slow">
          <CherryBlossom size={70} />
        </div>
        <div className="absolute bottom-10 right-10 opacity-15 animate-float" style={{ animationDelay: '2s' }}>
          <CherryBlossom size={60} />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(212, 165, 165, 0.2)', color: '#D4A5A5' }}>
              Client Stories
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              What Our Clients Say
            </h2>
            <div className="floral-divider w-24 mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card card-elevated">
                <div className="flex gap-1 mb-4 pt-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} className="fill-[#D4A5A5]" style={{ color: '#D4A5A5' }} />
                  ))}
                </div>
                <p className="text-[#4A5568] mb-6 leading-relaxed">
                  {testimonial.text}
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-[#D4A5A5]/20">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(212, 165, 165, 0.2)' }}>
                    <span className="font-serif font-medium" style={{ color: '#D4A5A5' }}>
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-[#2C2F36]">{testimonial.name}</p>
                    <p className="text-sm text-[#718096]">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(212, 165, 165, 0.2)', color: '#D4A5A5' }}>
                Common Questions
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-[#4A5568] mb-8">
                Have questions about our services or approach? Find answers to common questions below, 
                or contact us directly for more information.
              </p>
              <Link to="/contact" className="btn-pink">
                Contact Us
                <ArrowRight size={18} />
              </Link>
            </div>
            
            <div>
              <Accordion type="single" collapsible className="space-y-4">
                {faqs.map((faq) => (
                  <AccordionItem 
                    key={faq.id} 
                    value={`faq-${faq.id}`}
                    className="rounded-2xl px-6 border border-[#D4A5A5]/20"
                    style={{ background: 'linear-gradient(135deg, #FCF0F0 0%, white 100%)' }}
                  >
                    <AccordionTrigger className="text-left font-medium text-[#2C2F36] hover:no-underline py-4" style={{ '--tw-text-opacity': 1 }}>
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#4A5568] pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="section-padding" style={{ background: 'linear-gradient(180deg, white 0%, #FCF0F0 100%)' }}>
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(110, 158, 185, 0.15)', color: '#6E9EB9' }}>
                Visit Us
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-6">
                Located in Gordon, Nebraska
              </h2>
              <p className="text-[#4A5568] mb-8">
                Our welcoming clinic is conveniently located on Main Street in Gordon. 
                We look forward to meeting you and supporting your wellness journey.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-full" style={{ background: 'rgba(212, 165, 165, 0.2)' }}>
                    <MapPin size={20} style={{ color: '#D4A5A5' }} />
                  </div>
                  <div>
                    <p className="font-medium text-[#2C2F36]">Address</p>
                    <p className="text-[#4A5568]">{businessInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-full" style={{ background: 'rgba(212, 165, 165, 0.2)' }}>
                    <Clock size={20} style={{ color: '#D4A5A5' }} />
                  </div>
                  <div>
                    <p className="font-medium text-[#2C2F36]">Hours</p>
                    <p className="text-[#4A5568]">Mon, Wed-Fri: 9AM - 5PM</p>
                    <p className="text-[#4A5568]">Tue: 9AM - 7PM</p>
                    <p className="text-[#4A5568]">Sat: By Appointment</p>
                  </div>
                </div>
              </div>
              
              <Link to="/contact" className="btn-pink">
                Get Directions
                <ArrowRight size={18} />
              </Link>
            </div>
            
            <div className="rounded-3xl overflow-hidden shadow-xl h-80 lg:h-96 card-elevated">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2987.826728955098!2d-102.2063!3d42.8058!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x877e7c0fb6d04d99%3A0x2b5e5e5e5e5e5e5e!2s129%20N%20Main%20St%2C%20Gordon%2C%20NE%2069343!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Serenity Natural Health & Wellness Location"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Pink gradient */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #D4A5A5 0%, #E8C4C4 50%, #D4A5A5 100%)' }}>
        {/* Floating flowers */}
        <div className="absolute top-10 left-10 opacity-30 animate-float">
          <CherryBlossom size={60} color="white" />
        </div>
        <div className="absolute bottom-10 right-10 opacity-20 animate-float-slow">
          <CherryBlossom size={80} color="white" />
        </div>
        <div className="absolute top-1/2 left-1/4 opacity-15">
          <CherryBlossom size={50} color="white" />
        </div>
        
        <div className="container-custom text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6 text-white">
            Ready to Begin Your Wellness Journey?
          </h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-8">
            Take the first step towards optimal health. Attend a free orientation class or 
            schedule your personalized consultation today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-white text-[#D4A5A5] rounded-full font-semibold hover:bg-white/90 transition-all hover:scale-105 inline-flex items-center justify-center gap-2 shadow-lg"
            >
              Book Consultation
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/orientation" 
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
            >
              Free Orientation Class
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
